/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 4 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { useTranslation, untranslated } from "@i18n";
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState, type ComponentProps } from "react";
import { getViewerUrl, formCategories } from "@Helpers";
import { defaultSignatureModalState } from "./ReviewsConstants";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import DeleteIcon from "@Components/StickySidebarViewer/assets/DeleteIcon";
import CreateIcon from "@Components/StickySidebarViewer/assets/CreateIcon";
import OpenFileInViewerIcon from "@Assets/icons/OpenInViewer.svg";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import ResourceActivityInReview from "./ResourceActivityInReview";
import { Box } from "@mui/material";
import { portalCreateRoot } from "@CubeComponents/html/cellPortals";
import { flushSync } from "react-dom";

import {
  addResourcesToReview,
  fileStatusHelper,
  getFilesInReviewToolbar,
  getResourceDetails,
  getReviewData,
  getStatusBtnStyle,
  removeResourcesFromReview,
  updateFilesInReview,
} from "./ReviewsHelper";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { fetchListOfStatusesAvailableRequest } from "@Features/reviews/api/reviews.api";
import { getResourcesInReviewUrl } from "@Services/dataManager";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import ChangeFileStatusModal from "./ChangeFileStatusModal";
import FilePropertiesInReviews from "./FilePropertiesInReviews";
import DetailsIcon from "@Components/StickySidebarViewer/assets/Details";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import { showToastNotification } from "@Reducers/toastSlice";
import { FileManagerAsPopup } from "@Features/files";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ResourceStatusHistoryInReview from "./ResourceStatusHistoryInReview";
import { ReviewUpdateIcon, ReviewFileHistory } from "@Components/StickySidebarViewer/assets/ReviewUpdateIcon";
import AddSignatureToResource from "./AddSignatureToResource";
import IconComponent from "@CubeComponents/icons";
import Attachments from "./AddReferencedFileInReviews";
import ChecklistModal from "./ChecklistModal";
import _ from "lodash";
import {
  doExcelExportActivity,
  processDrawingNumbersAndDocDate,
  processProjectCustomFieldData,
  processReviewDataForAttachedFiles,
} from "./ReviewSheetForFichtner";
import { downloadFile } from "@Services/fileDownload";
import { setTransmittalDataForReviews } from "@Features/transmittals";
import Transmittals from "@Assets/icons/svg/TransmittalIcon.svg";
import axiosWrapper from "@AxiosWrapper";
import type { GetTransmittalDataByIdResponse } from "@ServerTypes/transmittals/transmittals.schema";
import type { GetFormTemplateResponse } from "@ServerTypes/portal/portal.schema";
import CreateTransmittal from "@Features/transmittals/pages/Transmitals/CreateTransmittal/CreateTransmittal";
import ChecklistAssignmentModal from "./ChecklistAssignmentModal";
import type { ChecklistAssignmentReviewDraft } from "./ChecklistAssignmentModal";
import Text from "@CubeComponents/html/Text/Text";
import { objectIdString, type ObjectIdString } from "@Helpers/schemas/common.schema";
import type { AutocompleteRenderInputParams } from "@mui/material";
import type {
  FetchListOfStatusesAvailableRequest,
  GetResourcesInReviewResponse,
  ReviewDataResult,
  ReviewStatusItem,
  UserPermissionsToMarkReviewResponse,
} from "@ServerTypes/reviews/reviews.schema";
import type { ReviewApprovalStage, ReviewResourceDescriptor, ReviewResourceToAttach } from "./ReviewsHelper";
import type { Status } from "./reviews.interfaces";
import type { ResourceSignatureEntry, ResourceStatusEntry } from "./ResourceStatusHistoryInReview";
import type { TransmittalReference } from "@Features/transmittals/hooks/transmittal.types";
import type { ReferencedFileRow } from "./AddReferencedFileInReviews";
import type {
  ColumnDefinition,
  GridRowBase,
  SlickGrid1Ref,
  ValidatedColumnDefinition,
} from "@CubeComponents/html/SlickGrid1";
import { RootState } from "@Reducers/index";

/** One element of `getResourcesInReviewResponse.result`. */
type ResourceInReviewItem = NonNullable<GetResourcesInReviewResponse["result"]>[number];

/**
 * SERVER SCHEMA GAP, reported: `resourceInReviewItemSchema.details` declares only the five
 * issue-oriented fields; the files grid reads six more off the same `.passthrough()` object.
 */
type FileInReviewDetails = ResourceInReviewItem["details"] & {
  name?: string;
  versionNumber?: number;
  size?: number;
  fileType?: string;
  isLatestVersion?: boolean;
  countOfIssues?: number;
};

/**
 * One row of the files-in-review grid.
 * SERVER SCHEMA GAP, reported: `resourceInReviewItemSchema` declares neither `fileVersionId` nor
 * `resourceType`/`signatures`/`referencedFiles`/`checklists`/`checklistAssignments`, all of which the
 * service returns (the schema is `.passthrough()`) and this grid reads.
 */
export type FileInReviewRow = ResourceInReviewItem &
  GridRowBase & {
    fileVersionId?: ObjectIdString;
    /**
     * The per-`resourceType` ids of the stored `resources` sub-document. `getPipelineForResources`
     * `$replaceRoot`s onto that sub-document, so every id `resourceSchema` accepts comes back on the
     * row; the same passthrough gap as `fileVersionId` hides them from the response schema.
     */
    estimateId?: ObjectIdString;
    estimateVersionId?: ObjectIdString;
    issueId?: ObjectIdString;
    taskId?: ObjectIdString;
    resourceType?: string;
    /**
     * Always present: `getPipelineForResources` ends in `{ $unwind: "$details" }` with no
     * `preserveNullAndEmptyArrays`, so a resource whose join found nothing is dropped from the page.
     */
    details: FileInReviewDetails;
    signatures?: ResourceSignatureEntry[];
    referencedFiles?: ReferencedFileRow[];
    checklists?: unknown[];
    checklistAssignments?: unknown[];
  };

/**
 * The status objects this grid hands to `ChangeFileStatusModal`. `Status` (reviews.interfaces) rather
 * than the server's `reviewStatusItemSchema` because the "no previous status" sentinel built below is
 * `{ _id: null, name: null, category: null }` — nulls the server schema cannot express.
 */
type FileStatusSelection = Partial<Status>;

interface FilesInReviewProps {
  /** Reviews `_id` — `objectIdString` in every reviews.schema.ts request this grid issues. */
  readonly reviewId: ObjectIdString;
  readonly spaceId: string;
  readonly projectId: string;
  /** `userPermissionsToMarkReviewResponse` verbatim — the four {status, errorMessage?} probes. */
  readonly enableBtns: UserPermissionsToMarkReviewResponse;
  readonly showProperties?: boolean;
  readonly refreshData: (refreshFiles: boolean, refreshIssues: boolean, refreshReviewData: boolean) => void;
  readonly allowFileAddition?: boolean;
  readonly allowFileUpdate?: boolean;
  readonly allowFileRemoval?: boolean;
  readonly allowBulkTransition?: boolean;
  readonly allowAddSignature?: boolean;
  readonly allowSendTransmittals?: boolean;
}

interface FileActivityModal {
  show: boolean;
}

interface AddFileModal {
  show: boolean;
  data: { resources: ReviewResourceToAttach[] } | null;
  stage: number;
}

interface StatusHistoryModal {
  show: boolean;
  statuses?: ResourceStatusEntry[];
  signatures?: ResourceSignatureEntry[];
}

interface SignatureModal {
  show: boolean;
  /** `defaultSignatureModalState.reviewId` really is `null` at rest. */
  reviewId?: string | null;
  statusTo?: FileStatusSelection;
  /** `ChangeFileStatusModal` declares `statusFrom?: Status | null`; null is a real value here. */
  statusFrom?: FileStatusSelection | null;
  resources?: ReviewResourceDescriptor[];
  type?: string;
  requireMandatoryComment?: boolean;
}

interface FileProperties {
  show: boolean;
  data:
    | (Omit<ComponentProps<typeof FilePropertiesInReviews>, "closeProperties"> & {
        /** Spread into the panel but not declared by it — harmless, kept verbatim. */
        fileVersionId?: ObjectIdString;
        resourceType?: string;
      })
    | null;
}

interface RemoveFileModal {
  show: boolean;
  resources: ReviewResourceDescriptor[];
}

interface BulkTransitionModal {
  show: boolean;
  fileVersionIds?: string[];
  data?: {
    resources: ReviewResourceDescriptor[];
    statusFrom?: FileStatusSelection;
    reviewId?: string;
    type?: string;
    requireMandatoryComment?: boolean;
  };
}

interface UpdateFileConfirmModal {
  show: boolean;
  /** File-version `_id`s — `objectIdString` in updateFilesInReviewRequest. */
  fileVersionIds: ObjectIdString[];
}

interface AddSignatureToResourceModal {
  show: boolean;
  data: { resources?: ReviewResourceDescriptor[]; reviewId?: ObjectIdString };
}

interface OpenAttachmentModal {
  show: boolean;
  fileVersionId?: ObjectIdString;
  attachments?: ReferencedFileRow[];
}

/** The wizard draft `resourceSelectionCallback` builds for `ChecklistAssignmentModal`. */
interface ChecklistFlowData extends ChecklistAssignmentReviewDraft {
  resources: ReviewResourceToAttach[];
  reviewFlow: {
    checklists: NonNullable<ReviewDataResult["reviewFlow"]>["checklists"];
    reviewLevels: NonNullable<ReviewDataResult["reviewFlow"]>["reviewLevels"];
  };
  checklistAssignments: never[];
}

/** `processProjectCustomFieldData`'s own result shape (its `ProjectCustomFieldResult` is module-private). */
type ProjectCustomField = ReturnType<typeof processProjectCustomFieldData>;

interface ChecklistModalState {
  show: boolean;
  fileVersionId: ObjectIdString | null;
  /** Set from the row and never read back; a review without checklists simply has none. */
  checklists: unknown[] | undefined;
}

interface FileSizeOption {
  name: string;
  divideValue: number;
}

/**
 * The transmittal reference rows this screen builds out of the selected grid rows. Declared as
 * `TransmittalReference` itself, which `CreateTransmittal` then takes without an assertion: unlike
 * the file-manager grids that hand their raw rows over, this screen builds every field. `remarks`
 * starts empty because the transmittal form is where it is filled in (`useTransmittalReferences`
 * defaults it with `|| ""` either way).
 */
type ProcessedTransmittalRecord = TransmittalReference;

/**
 * `Status` (reviews.interfaces) declares every field required-and-nullable, while this grid holds
 * them as `Partial<Status>` because `defaultSignatureModalState` seeds `{}` at rest. Filling the
 * absent keys with `null` is the same value `ChangeFileStatusModal` reads today — it treats a missing
 * key and a `null` one alike ("no status").
 */
const toStatus = (status: FileStatusSelection | undefined): Status => ({
  ...status,
  _id: status?._id ?? null,
  name: status?.name ?? null,
  category: status?.category ?? null,
});

const FilesInReview = forwardRef<SlickGrid1Ref<FileInReviewRow>, FilesInReviewProps>(
  (
    {
      reviewId,
      spaceId,
      projectId,
      enableBtns,
      showProperties = false,
      refreshData,
      allowFileAddition = true,
      allowFileUpdate = true,
      allowFileRemoval = true,
      allowBulkTransition = false,
      allowAddSignature = false,
      allowSendTransmittals = false,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    // `forwardRef` also admits a callback ref or none at all; only an object ref can be read back,
    // which is why every `dataGrid.current` access below is guarded.
    const dataGrid = typeof ref === "object" && ref !== null ? ref : null;
    const dispatch = useDispatch();

    // `FilesInReviewProps.projectId` is a plain string (callers read it off the route); ChecklistModal
    // declares the 24-hex brand, so it is parsed once here rather than asserted at the call site.
    const parsedProjectId = objectIdString.safeParse(projectId).data;

    const fileSizeOptions: FileSizeOption[] = [
      { name: "KB", divideValue: 1024 },
      { name: "MB", divideValue: 1048576 },
    ];

    const userDetails = useSelector((state: RootState) => state.userDetails);
    // The `reviewData` slice is declared `ReviewDataResult` with an `{}` pre-load placeholder, so
    // every field is optional there while this screen only mounts once `getReviewData` has populated
    // it. The fix belongs in the slice — src/reducers/ReviewFlows.ts, the way contracts.reducer.ts
    // already types `ContractDetailsState` — not in each consumer.
    const reviewData = useSelector(
      (state: RootState) =>
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see the note above
        state.reviewData as ReviewDataResult & {
          approvalStages: NonNullable<ReviewDataResult["approvalStages"]>;
          reviewStatus: NonNullable<ReviewDataResult["reviewStatus"]>;
          reviewFlow: NonNullable<ReviewDataResult["reviewFlow"]>;
        },
    );
    // The slice is `createSlice({ initialState: null })` with no declared state type, so the store
    // types this `null` while `setTransmittalDataForReviews(true)` really does put a boolean in it.
    // Widened on selection so the effect that reads it below stays live; the fix belongs in
    // features/transmittals/state/transmitals.reducer.ts.
    const transmittalDataForReviews = useSelector(
      (state: RootState): boolean | null => state.transmittalDataForReviews,
    );

    const [isLoadingComplete, setIsLoadingComplete] = useState<boolean>(false);
    const [fileActivityModal, setFileActivityModal] = useState<FileActivityModal>({ show: false });
    const [addFileModal, setAddFileModal] = useState<AddFileModal>({
      show: false,
      data: null,
      stage: 1,
    });
    const [statusHistoryModal, setStatusHistoryModal] = useState<StatusHistoryModal>({
      show: false,
    });
    const [signatureModal, setSignatureModal] = useState<SignatureModal>(defaultSignatureModalState);
    const [fileProperties, setFileProperties] = useState<FileProperties>({
      show: false,
      data: null,
    });
    const [removeFileModal, setRemoveFileModal] = useState<RemoveFileModal>({
      show: false,
      resources: [],
    });
    const [bulkTransitionModal, setBulkTransitionModal] = useState<BulkTransitionModal>({
      show: false,
      fileVersionIds: [],
    });
    const [updateFileConfirmModal, setUpdateFileConfirmModal] = useState<UpdateFileConfirmModal>({
      show: false,
      fileVersionIds: [],
    });
    const [addSignatureToResourceModal, setAddSignatureToResourceModal] = useState<AddSignatureToResourceModal>({
      show: false,
      data: {},
    });
    const [openAttachmentModal, setOpenAttachmentModal] = useState<OpenAttachmentModal>({
      show: false,
    });
    const [projectCustomField, setProjectCustomField] = useState<ProjectCustomField>({
      status: false,
      data: [],
    });
    const [checklistModal, setChecklistModal] = useState<ChecklistModalState>({
      show: false,
      fileVersionId: null,
      checklists: [],
    });
    const [propertiesBtnDisabled, setPropertiesBtnDisabled] = useState<boolean>(true);
    const [isLoadingFileUpdate, setIsLoadingFileUpdate] = useState<boolean>(false);
    const [isLoadingAddFiles, setIsLoadingAddFiles] = useState<boolean>(false);
    const [isLoadingRemoveFiles, setIsLoadingRemoveFiles] = useState<boolean>(false);
    const [openCreateTransmittalModal, setOpenCreateTransmittalModal] = useState<boolean>(false);
    const [processedTransmittalRecords, setProcessedTransmittalRecords] = useState<ProcessedTransmittalRecord[]>([]);
    // `ChecklistAssignmentModal` is generic over its draft shape; this is the draft handed to it.
    const [checklistFlowData, setChecklistFlowData] = useState<ChecklistFlowData | null>(null);
    const [statusOptionsCache, setStatusOptionsCache] = useState<Record<string, ReviewStatusItem[]>>({});

    const portalData = useSelector((state: RootState) => state.portalData);
    const projectData = useSelector((state: RootState) => state.projectData);

    const reviewDataHelperRef = useRef(reviewData);

    const filesQuery = useMemo(
      () => ({
        params: {
          resourceTypes: ["RESOURCE_TYPE_FILE", "RESOURCE_TYPE_EMBED"],
          reviewId,
        },
      }),
      [reviewId],
    );

    const addBtnTemplate = useCallback(
      () => (
        <div className="me-[1.875rem] bg-transparent">
          <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} id="add_file">
            <span className="flex flex-row items-center justify-start">
              <CreateIcon size="15" className="me-1" />
              <Text>{t("Add")}</Text>
            </span>
          </ButtonComponent>
        </div>
      ),
      [],
    );

    const updateBtnTemplate = useCallback(
      () => (
        <div className="me-[1.875rem] bg-transparent">
          <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="update_file">
            <span className="flex flex-row items-center justify-start">
              {(ReviewUpdateIcon as (props: { size: string; className: string }) => React.ReactNode)({
                size: "15",
                className: "me-1",
              })}
              <Text>{t("Update")}</Text>
            </span>
          </ButtonComponent>
        </div>
      ),
      [],
    );

    const removeBtnTemplate = useCallback(
      () => (
        <div className="me-[1.875rem] bg-transparent">
          <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="remove_file">
            <span className="flex flex-row items-center justify-start">
              <DeleteIcon size="15" className="me-1" />
              <Text>{t("Remove")}</Text>
            </span>
          </ButtonComponent>
        </div>
      ),
      [],
    );

    const fileActivityBtnTemplate = useCallback(
      () => (
        <div className="me-[1.875rem] bg-transparent">
          <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="file_activity">
            <span className="flex flex-row items-center justify-start">
              <ReviewFileHistory size="15" className="me-1" {...({} as ComponentProps<typeof ReviewFileHistory>)} />
              <Text>{t("Show File History")}</Text>
            </span>
          </ButtonComponent>
        </div>
      ),
      [],
    );

    const bulkTransitionBtnTemplate = useCallback(
      () => (
        <div className="me-[1.875rem] bg-transparent">
          <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="bulk_transition">
            <span className="flex flex-row items-center justify-start">
              <IconComponent name="Sync" width="0.9375rem" height="0.9375rem" className="me-1" />
              <Text>{t("Bulk Transition")}</Text>
            </span>
          </ButtonComponent>
        </div>
      ),
      [],
    );

    const addSignatureToFileBtnTemplate = useCallback(
      () => (
        <div className="me-[1.875rem] bg-transparent">
          <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="add_signature">
            <span className="flex flex-row items-center justify-start">
              <IconComponent name="inPdfSignature" width="0.9375rem" height="0.9375rem" className="me-1" />
              <Text>{t("Add Signature")}</Text>
            </span>
          </ButtonComponent>
        </div>
      ),
      [],
    );

    const excelExportReviews = useCallback(() => {
      if (!enableBtns.approve.status) return null;

      return (
        <div className="me-[1.875rem] bg-transparent">
          <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="excel_export_reviews">
            <span className="flex flex-row items-center justify-start">
              <img src={Transmittals} className="me-2" alt={t("Transmittals")} />
              <Text>{t("Send via Transmittals")}</Text>
            </span>
          </ButtonComponent>
        </div>
      );
    }, [enableBtns.approve.status]);

    const downloadReviewsFiles = useCallback(
      () => (
        <div className="me-[1.875rem] bg-transparent">
          <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="download_reviews_files">
            <span className="flex flex-row items-center justify-start">
              <IconComponent name="download" width="1.5rem" height="1.5rem" className="me-2" />
              <Text>{t("Download")}</Text>
            </span>
          </ButtonComponent>
        </div>
      ),
      [],
    );

    const processRecordsForTransmittals = () => {
      // `getAllSelectedItems()` is declared to return rows, but the dataView pushes `getItemById(id)`
      // for every id still selected, and that is `undefined` for a selection kept across a refresh or
      // a filter change; the grid's own placeholder rows are dropped alongside those holes.
      const selectedRows: Array<FileInReviewRow | undefined> = dataGrid?.current?.dataView.getAllSelectedItems() ?? [];
      const selectedRecords = selectedRows.filter(
        (record): record is FileInReviewRow & { fileVersionId: ObjectIdString } =>
          record != null && !record.__isDummy && Boolean(record.fileVersionId),
      );
      // The `details` members below are the ones the filter cannot promise; the grid only ever renders
      // a row when they are present, so `!` is kept for them alone.
      const processedRecords: ProcessedTransmittalRecord[] = selectedRecords.map((record) => ({
        fileVersion: record.fileVersionId,
        resourceType: record.resourceType!,
        name: record.details.name!,
        versionNumber: record.details.versionNumber!,
        size: record.details.size!,
        dateCreated: record.addedDate!,
        fileType: record.details.fileType!,
        remarks: "",
      }));
      setProcessedTransmittalRecords(processedRecords);
      return processedRecords.length;
    };

    const toolbar = useMemo(
      () =>
        getFilesInReviewToolbar({
          addBtnTemplate,
          updateBtnTemplate,
          removeBtnTemplate,
          fileActivityBtnTemplate,
          bulkTransitionBtnTemplate,
          addSignatureToFileBtnTemplate,
          excelExportReviews,
          downloadReviewsFiles,
          allowSendTransmittals,
        }),
      [
        allowSendTransmittals,
        addBtnTemplate,
        updateBtnTemplate,
        removeBtnTemplate,
        fileActivityBtnTemplate,
        bulkTransitionBtnTemplate,
        addSignatureToFileBtnTemplate,
        excelExportReviews,
        downloadReviewsFiles,
      ],
    );

    const closeStatusHistory = () => {
      setStatusHistoryModal({ show: false });
    };

    const openStatusHistoryFromData = (rowData: FileInReviewRow) => {
      // `resourceInReviewItemSchema.statuses` declares only `name`; the rest arrives via its
      // passthrough (SERVER SCHEMA GAP, reported). The assertion is erased.
      const { statuses, signatures } = rowData as {
        statuses: ResourceStatusEntry[];
        signatures?: ResourceSignatureEntry[];
      };
      if (statuses.length > 1 || signatures?.length) {
        setStatusHistoryModal({ show: true, statuses, signatures });
      }
    };

    // Assigned to `container.onclick`, so it receives the DOM MouseEvent, not React's synthetic one.
    const openFileInViewer = (e: MouseEvent) => {
      // `currentTarget` is `EventTarget | null` on the DOM event; `dataset` only exists on an element.
      const target = e.currentTarget;
      if (!(target instanceof HTMLElement)) {
        return;
      }
      const fileVersionId = target.dataset.fileversionid || "";

      const viewerUrl = getViewerUrl({ fileVersionId, spaceId, projectId });
      window.open(viewerUrl, "_blank");
    };

    const closeSignatureModal = (shouldRefreshGrid: boolean = false) => {
      if (shouldRefreshGrid) {
        refreshData(true, false, false);
      }

      setSignatureModal(defaultSignatureModalState);
    };

    const openChecklistModalFromData = (rowData: FileInReviewRow) => {
      setChecklistModal({
        show: true,
        // Every grid row is a file/embed resource, so `fileVersionId` is always present. `!` is erased.
        fileVersionId: rowData.fileVersionId!,
        checklists: rowData.checklists,
      });
    };

    const closeChecklistModal = () => {
      setChecklistModal({ show: false, fileVersionId: null, checklists: [] });
      refreshData(true, true, true);
    };

    const fileStatusChanged = (
      newValue: ReviewStatusItem,
      previousStatus: FileStatusSelection | undefined,
      // Branded: `resourceSchema.fileVersionId` is `objectIdString`, and every caller reads this
      // off a grid row whose own type already declares it `ObjectIdString`.
      fileVersionId: ObjectIdString,
      resourceType: string,
    ) => {
      const statusTo = newValue;
      const resources = [{ fileVersionId, resourceType }];

      const statusFrom = previousStatus || {
        _id: null,
        name: null,
        category: null,
      };

      const { approvalStages } = reviewDataHelperRef.current;
      const currentStage = approvalStages.find((stage: ReviewApprovalStage) => stage.clearanceStatus === "in_progress");
      // KNOWN BUG, preserved verbatim: `requireMandatoryComment` is optional on
      // `approvalStageSchema`, so a stage saved before that setting existed throws here
      // ("Cannot read properties of undefined (reading 'enabled')"). `!` is erased.
      const requireMandatoryComment = currentStage
        ? currentStage.requireMandatoryComment!.enabled && currentStage.requireMandatoryComment!.values.includes(4)
        : false;

      setSignatureModal({
        show: true,
        reviewId,
        statusTo,
        statusFrom,
        resources,
        type: "standardTransition",
        requireMandatoryComment,
      });
    };

    const fetchStatusOptionsForFile = async (fileVersionId: string, resource: ReviewResourceDescriptor) => {
      if (statusOptionsCache[fileVersionId]) return statusOptionsCache[fileVersionId];
      try {
        // Brand boundary: `fileStatusHelper` yields the schema's INPUT shape (plain id strings) while
        // `FetchListOfStatusesAvailableRequest` is the `z.infer` output shape (branded ObjectIdString).
        // The assertion is erased; the wire bytes are unchanged.
        const response = await fetchListOfStatusesAvailableRequest({
          value: { params: { reviewId, resource } },
        } as FetchListOfStatusesAvailableRequest);
        if (response.data.result) {
          const statusOptions = response.data.result;
          setStatusOptionsCache((prev) => ({ ...prev, [fileVersionId]: statusOptions }));
          return statusOptions;
        }
      } catch (e) {
        console.error("Error fetching status options:", e);
        Promise.reject(e);
      }
      return [];
    };

    const openFileProperties = (selectedRecord: FileInReviewRow) => {
      const { statuses, fileVersionId, resourceType, details } = selectedRecord as FileInReviewRow & {
        statuses: ResourceStatusEntry[];
      };
      const { name: fileName, versionNumber: fileVersionNumber, size } = details;
      // KNOWN BUG, preserved verbatim: a resource whose joined `details.name` is missing throws
      // ("Cannot read properties of undefined (reading 'split')"). Both `!`s are erased.
      const fileType = fileName!.split(".").slice(-1)[0]!.toUpperCase();

      setFileProperties({
        show: true,
        data: {
          fileVersionId,
          fileName,
          fileType,
          fileVersionNumber,
          statuses,
          size,
          resourceType,
        },
      });
    };

    const closeFileProperties = () => {
      setFileProperties({ show: false, data: null });
    };

    const closeAddFileModal = () => {
      setAddFileModal({ show: false, data: null, stage: 1 });
    };

    const addFileInReview = () => {
      setAddFileModal({ show: true, data: null, stage: 1 });
    };

    const backToFileSelection = () => {
      setIsLoadingAddFiles(false);
      setAddFileModal((prev) => ({
        ...prev,
        stage: 1,
        data: null,
      }));
    };

    const handleChecklistAssignment = async (updatedData: ChecklistFlowData) => {
      setIsLoadingAddFiles(true);
      const response = await addResourcesToReview({
        resources: updatedData.resources,
        reviewId,
      });
      setIsLoadingAddFiles(false);
      closeAddFileModal();
      handleToastNotification(response);
    };

    const updateFileVersionInReview = (fileVersionIds: ObjectIdString[]) => {
      setUpdateFileConfirmModal({ show: true, fileVersionIds });
    };

    const closeUpdateFileModal = () => {
      setUpdateFileConfirmModal({ show: false, fileVersionIds: [] });
    };

    const closeRemoveFileModal = () => {
      setRemoveFileModal({ show: false, resources: [] });
    };

    const closeAttachmentModal = () => {
      setOpenAttachmentModal({ show: false });
      closeSignatureModal(true);
    };

    const getResourceDetailsForRemoval = (resources: FileInReviewRow[]): ReviewResourceDescriptor[] =>
      resources
        .map((resource): ReviewResourceDescriptor | undefined => {
          const { resourceType } = resource;

          if (resourceType === "RESOURCE_TYPE_FILE" || resourceType === "RESOURCE_TYPE_EMBED") {
            const { fileVersionId } = resource;
            return { fileVersionId, resourceType };
          }

          if (resourceType === "RESOURCE_TYPE_ESTIMATE") {
            const { estimateId, estimateVersionId } = resource;
            return { estimateId, estimateVersionId, resourceType };
          }

          if (resourceType === "RESOURCE_TYPE_ISSUE") {
            const { issueId } = resource;
            return { issueId, resourceType };
          }

          if (resourceType === "RESOURCE_TYPE_TASKS") {
            const { taskId } = resource;
            return { taskId, resourceType: "RESOURCE_TYPE_TASK" };
          }

          // An unrecognised `resourceType` has no id field to send. It used to fall off the end as
          // `undefined` and be sent inside `resources[]`, which failed the schema's
          // `z.array(resourceSchema)` validation for the WHOLE request; it is dropped instead.
          return undefined;
        })
        .filter((resource) => resource !== undefined);

    const removeFileFromReview = (selectedRecords: FileInReviewRow[]) => {
      const resources = getResourceDetailsForRemoval(selectedRecords);
      setRemoveFileModal({ show: true, resources });
    };

    const openFileActivity = () => {
      setFileActivityModal({ show: true });
    };

    const closeFileActivityModal = () => {
      setFileActivityModal({ show: false });
    };

    const downLoadReviewFiles = (fileVersionIds: string[]) => {
      downloadFile({
        fileVersionIds,
        folderIds: [],
        mode: "download",
      });
    };

    const getStatusFromForResources = (records: FileInReviewRow[]): FileStatusSelection => {
      const statusFromSet = new Set();

      records.map((record) => {
        // KNOWN BUG, preserved verbatim: a resource with an empty `statuses` array throws here.
        // `_id` comes from the same passthrough gap as elsewhere. Both erased.
        const currentStatus = (record.statuses.slice(-1)[0] as ResourceStatusEntry | undefined)!._id;
        if (!statusFromSet.has(currentStatus)) {
          statusFromSet.add(currentStatus);
        }
      });

      const statusFromList = Array.from(statusFromSet);

      if (statusFromList.length > 1) {
        return { _id: "Varies", name: "Varies", category: "Varies" };
      }

      // `resourceInReviewItemSchema.statuses` declares only `name`, so `_id`/`category` come off the
      // element's passthrough index signature as `unknown` (SERVER SCHEMA GAP, reported). The
      // annotation states the shape the service actually writes; no assertion is needed because every
      // member of `ResourceStatusEntry` is optional.
      const status: ResourceStatusEntry | undefined = records[0]?.statuses.slice(-1)[0];
      return { _id: status?._id, name: status?.name, category: status?.category };
    };

    const executeBulkTransition = (selectedRecords: FileInReviewRow[]) => {
      const { approvalStages } = reviewDataHelperRef.current;
      const currentStage = approvalStages.find((stage: ReviewApprovalStage) => stage.clearanceStatus === "in_progress");
      // Same optional-`requireMandatoryComment` dereference as in `fileStatusChanged`; `!` is erased.
      const requireMandatoryComment = currentStage
        ? currentStage.requireMandatoryComment!.enabled && currentStage.requireMandatoryComment!.values.includes(4)
        : false;

      const resources = getResourceDetailsForRemoval(selectedRecords);
      const statusFrom = getStatusFromForResources(selectedRecords);
      setBulkTransitionModal({
        show: true,
        data: {
          resources,
          statusFrom,
          reviewId,
          type: "bulkTransition",
          requireMandatoryComment,
        },
      });
    };

    const closeBulkTransitionModal = (shouldRefreshGrid: boolean = false) => {
      if (shouldRefreshGrid) {
        refreshData(true, false, false);
      }

      setBulkTransitionModal({ show: false, data: { resources: [] } });
    };

    const addSignatureToResource = async (selectedRecords: FileInReviewRow[]) => {
      const resources = getResourceDetailsForRemoval(selectedRecords);
      setAddSignatureToResourceModal({
        show: true,
        data: { resources, reviewId },
      });
    };

    // Optional argument, because `AddSignatureToResource` declares `closeModal: (refresh?: boolean)`.
    const closeAddSignatureModal = (shouldRefreshGrid: boolean = false) => {
      setAddSignatureToResourceModal({ show: false, data: {} });

      if (shouldRefreshGrid) {
        refreshData(true, false, false);
      }
    };

    const toolbarClickHandler = useCallback(
      (toolClicked: string) => {
        const selectedRecords = dataGrid?.current?.dataView.getAllSelectedItems() || [];

        if (toolClicked === "add_file") {
          addFileInReview();
        }

        if (toolClicked === "update_file" && selectedRecords.length >= 1) {
          const fileVersionIds = selectedRecords.map((record: FileInReviewRow) => record.fileVersionId!);
          updateFileVersionInReview(fileVersionIds);
        }

        if (toolClicked === "remove_file" && selectedRecords.length >= 1) {
          removeFileFromReview(selectedRecords);
        }

        if (toolClicked === "file_activity") {
          openFileActivity();
        }
        if (toolClicked === "excel_export_reviews" && selectedRecords.length >= 1 && allowSendTransmittals) {
          const count = processRecordsForTransmittals();
          if (count > 0) setOpenCreateTransmittalModal(true);
        }
        if (toolClicked === "download_reviews_files" && selectedRecords.length >= 1) {
          const fileVersionIds = selectedRecords.map((record: FileInReviewRow) => record.fileVersionId!);
          downLoadReviewFiles(fileVersionIds);
        }
        if (toolClicked === "bulk_transition" && selectedRecords.length > 1 && allowBulkTransition) {
          executeBulkTransition(selectedRecords);
        }

        if (toolClicked === "add_signature" && selectedRecords.length >= 1 && allowAddSignature) {
          addSignatureToResource(selectedRecords);
        }

        if ((toolClicked === "file_properties" || toolClicked === "properties") && selectedRecords.length === 1) {
          openFileProperties(selectedRecords[0]!);
        }
      },

      [allowSendTransmittals, allowBulkTransition, allowAddSignature, reviewId],
    );

    const selectionEvents = () => {
      const grid = dataGrid?.current;
      if (grid) {
        const selectedRecords = grid.dataView.getAllSelectedItems();

        const removeBtnState = selectedRecords.length >= 1;
        const updateBtnState = removeBtnState;
        const bulkTransitionState = allowBulkTransition && selectedRecords.length > 1;
        const addSignatureState = allowAddSignature && selectedRecords.length >= 1;
        const downloadButton = selectedRecords.length >= 1;
        const addTransmittalExcelState = allowSendTransmittals && selectedRecords.length >= 1;

        setPropertiesBtnDisabled(selectedRecords.length !== 1);

        allowFileRemoval && grid.toolbarModule?.enableItems(["remove_file"], removeBtnState);
        allowFileUpdate && grid.toolbarModule?.enableItems(["update_file"], updateBtnState);
        grid.toolbarModule?.enableItems(["bulk_transition"], bulkTransitionState);
        grid.toolbarModule?.enableItems(["add_signature"], addSignatureState);
        grid.toolbarModule?.enableItems(["excel_export_reviews"], addTransmittalExcelState);
        grid.toolbarModule?.enableItems(["download_reviews_files"], downloadButton);

        if (selectedRecords.length !== 1) {
          closeFileProperties();
        }
      }
    };

    const dataBound = (ready: boolean) => {
      setIsLoadingComplete(ready);
    };

    const handleToastNotification = (response: { data: { status?: boolean; errorMessage?: string } }) => {
      if (response.data.status) {
        dispatch(
          showToastNotification({
            content: t("The selected files have been added to the review"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        refreshData(true, true, true);
        return;
      }

      // The raw server `errorMessage` is server copy, not a translation key, so it goes through
      // `untranslated()` like the sibling toasts in this file.
      const content = response.data.errorMessage
        ? untranslated(response.data.errorMessage)
        : t("The file(s) could not be added to the review");
      dispatch(showToastNotification({ content, type: TOAST_TYPE_ERROR }));
    };

    const resourceSelectionCallback = async (listOfFiles: Record<string, unknown>) => {
      const resources = Object.values(listOfFiles);
      const resourcesMap = getResourceDetails(resources);

      if (!resourcesMap.length) {
        dispatch(showToastNotification({ content: t("Please add atleast one file"), type: TOAST_TYPE_ERROR }));
        return;
      }
      const hasChecklist = reviewData.reviewFlow.checklists;

      if (hasChecklist) {
        const normalized = {
          resources: resourcesMap,
          reviewFlow: {
            checklists: reviewData.reviewFlow.checklists,
            reviewLevels: reviewData.reviewFlow.reviewLevels,
          },
          checklistAssignments: [],
        };

        setChecklistFlowData(normalized);
        setAddFileModal((prev) => ({ ...prev, show: true, stage: 2, data: { resources: resourcesMap } }));
        return;
      }

      setIsLoadingAddFiles(true);
      const response = await addResourcesToReview({
        resources: resourcesMap,
        reviewId,
      });
      setIsLoadingAddFiles(false);
      closeAddFileModal();
      handleToastNotification(response);
    };

    const removeFilesFromReview = async () => {
      const { resources } = removeFileModal;
      setIsLoadingRemoveFiles(true);
      const response = await removeResourcesFromReview({ reviewId, resources });
      setIsLoadingRemoveFiles(false);
      closeRemoveFileModal();

      if (response.data.status) {
        dispatch(
          showToastNotification({
            content: t("The selected files have been removed from the review"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        refreshData(true, true, true);
        return;
      }

      const content = response.data.errorMessage
        ? untranslated(response.data.errorMessage)
        : t("The file(s) could not be removed from the review");
      dispatch(showToastNotification({ content, type: TOAST_TYPE_ERROR }));
    };

    const updateFilesInReviewHandler = async () => {
      const { fileVersionIds } = updateFileConfirmModal;
      setIsLoadingFileUpdate(true);

      const response = await updateFilesInReview({ reviewId, fileVersionIds });
      setIsLoadingFileUpdate(false);
      closeUpdateFileModal();

      if (response.data.status) {
        dispatch(
          showToastNotification({
            content: t("The selected files have been updated to their latest version"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
        refreshData(true, true, true);
        return;
      }

      const content = response.data.errorMessage
        ? untranslated(response.data.errorMessage)
        : t("The file(s) could not be updated in the review");
      dispatch(showToastNotification({ content, type: TOAST_TYPE_ERROR }));
    };

    const getFormTemplate = useCallback(async () => {
      const response = await axiosWrapper.post<GetFormTemplateResponse>(`${getBackendUrl()}/portal/getFormTemplate`, {
        resourceType: formCategories.projects,
        projectId,
      });

      if (response.data.status && response.data.data) {
        const processedData = processProjectCustomFieldData(response.data.data);
        setProjectCustomField(processedData);
      }
    }, [projectId]);

    useEffect(() => {
      getFormTemplate();
    }, [getFormTemplate]);

    useEffect(() => {
      reviewDataHelperRef.current = reviewData;
    }, [reviewData]);

    useEffect(() => {
      if (!isLoadingComplete) return;
      const slickGrid = dataGrid?.current?.slickGrid;
      if (!slickGrid) return;
      slickGrid.invalidate();
      slickGrid.render();
    }, [reviewData.reviewStatus, reviewData.reviewFlow.checklists, dataGrid, isLoadingComplete]);

    useEffect(() => {
      const grid = dataGrid?.current;
      if (grid && isLoadingComplete) {
        const selectedRecords = grid.dataView.getAllSelectedItems();

        const removeBtnState = !!(allowFileRemoval && selectedRecords.length >= 1);
        const updateBtnState = !!(allowFileUpdate && selectedRecords.length >= 1);
        const addBtnState = !!allowFileAddition;
        const bulkTransitionState = !!(selectedRecords.length > 1 && allowBulkTransition);
        const addSignatureState = !!(selectedRecords.length >= 1 && allowAddSignature);
        const addTransmittalExcelState = !!(selectedRecords.length >= 1 && allowSendTransmittals);
        const downloadButton = !!(selectedRecords.length >= 1);

        grid.toolbarModule?.enableItems(["bulk_transition"], bulkTransitionState);
        grid.toolbarModule?.enableItems(["add_signature"], addSignatureState);
        grid.toolbarModule?.enableItems(["excel_export_reviews"], addTransmittalExcelState);
        grid.toolbarModule?.enableItems(["remove_file"], removeBtnState);
        grid.toolbarModule?.enableItems(["update_file"], updateBtnState);
        grid.toolbarModule?.enableItems(["download_reviews_files"], downloadButton);
        grid.toolbarModule?.enableItems(["add_file"], addBtnState);
      }
    }, [
      isLoadingComplete,
      showProperties,
      allowFileAddition,
      allowFileUpdate,
      allowFileRemoval,
      allowBulkTransition,
      allowAddSignature,
      allowSendTransmittals,
      dataGrid,
    ]);

    const downloadTransmittalDetailsInExcels = async () => {
      const response = await getReviewData({ _id: reviewId });
      const result = processReviewDataForAttachedFiles(response.data.result, userDetails);
      const transmittalData = await axiosWrapper.post<GetTransmittalDataByIdResponse>(
        `${getBackendUrl()}/transmittals/getTransmittalDataById`,
        {
          transmittalId: result.latestAttachedToTransmittal.transmittalId,
        },
      );
      const data = processDrawingNumbersAndDocDate(transmittalData.data.metadata?.files || []);
      dispatch(setTransmittalDataForReviews(null));
      doExcelExportActivity({
        projectData,
        filesDetails: data,
        indexAtProjectLevel: result.latestAttachedToTransmittal.transmittalIndexAtProjectLevel,
        subject: transmittalData.data.data?.subject || "",
        projectCustomField: projectCustomField.data,
      });
    };

    useEffect(() => {
      if (
        transmittalDataForReviews &&
        projectCustomField.status &&
        // `frontendUrl` is optional on the portal slice; `includes(undefined)` is false either way.
        ["fichtnerindia.app.cube.ms", "techture.staging.cube.ms"].includes(portalData.frontendUrl!)
      ) {
        downloadTransmittalDetailsInExcels();
      }
    }, [transmittalDataForReviews, projectCustomField.status]);

    const openFileInViewerFormatter = (
      row: number,
      cell: number,
      value: unknown,
      columnDef: unknown,
      dataContext: FileInReviewRow,
    ) => {
      const container = document.createElement("div");
      container.className = "cursor-pointer";
      container.dataset.fileversionid = dataContext.fileVersionId;
      container.innerHTML = `<img src="${OpenFileInViewerIcon}" alt="${t("Open in Viewer")}" />`;
      container.onclick = openFileInViewer;
      return container;
    };

    const fileTypeFormatter = (
      row: number,
      cell: number,
      value: unknown,
      columnDef: unknown,
      dataContext: FileInReviewRow,
    ) => {
      return dataContext.details.fileType || "";
    };

    const _dropdownItemTemplate = useCallback((args: ReviewStatusItem) => {
      const style = getStatusBtnStyle(args.category);
      return (
        <div className="py-2 px-3">
          <span style={style} className="rounded py-1 px-4 text-sm text-white">
            {args.name}
          </span>
        </div>
      );
    }, []);

    const openReferencedFilesAttachedFromData = (rowData: FileInReviewRow) => {
      setOpenAttachmentModal({
        show: true,
        fileVersionId: rowData.fileVersionId,
        attachments: rowData.referencedFiles,
      });
    };

    const FileStatusDropdown = ({ args }: { args: FileInReviewRow }) => {
      // A row whose `statuses` array is empty has no current status; `fileStatusChanged` already
      // declares its `previousStatus` optional for exactly that case.
      const status = args.statuses.slice(-1)[0];
      const [localStatusOptions, setLocalStatusOptions] = useState<ReviewStatusItem[]>(
        statusOptionsCache[args.fileVersionId!] || [],
      );

      useEffect(() => {
        if (statusOptionsCache[args.fileVersionId!]) {
          setLocalStatusOptions(statusOptionsCache[args.fileVersionId!] ?? []);
          return;
        }
        let cancelled = false;
        const fetchOptions = async () => {
          const resource = fileStatusHelper(args);
          const options: ReviewStatusItem[] = await fetchStatusOptionsForFile(args.fileVersionId!, resource);
          if (!cancelled) {
            setLocalStatusOptions(Array.isArray(options) ? options : []);
          }
        };
        fetchOptions();
        return () => {
          cancelled = true;
        };
      }, [args.fileVersionId]);

      const selectedOption = localStatusOptions.find((opt: ReviewStatusItem) => opt._id === status?._id) || null;
      const isDraft = reviewDataHelperRef.current.reviewStatus === "Draft";
      const isStatusDisabled = isDraft || localStatusOptions.length === 0;

      return (
        <div className="flex flex-row items-center gap-4">
          <AutoComplete
            options={localStatusOptions}
            valueField="_id"
            width={"12.5rem"}
            openOnFocus={!isStatusDisabled}
            disabled={isStatusDisabled}
            getOptionLabel={(option: ReviewStatusItem) => option.name || ""}
            value={selectedOption}
            onChange={(_event: React.SyntheticEvent, newValue: ReviewStatusItem | null) => {
              if (newValue) {
                fileStatusChanged(newValue, status, args.fileVersionId!, args.resourceType!);
              }
            }}
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: ReviewStatusItem) => (
              <li {...props} style={{ ...props.style, paddingInlineStart: "0.25rem", paddingInlineEnd: "0.25rem" }}>
                <div className="py-2 px-1">
                  <span
                    style={getStatusBtnStyle(option.category)}
                    className="whitespace-nowrap rounded py-1 px-4 text-sm text-white"
                  >
                    {option.name}
                  </span>
                </div>
              </li>
            )}
            renderInput={(params: AutocompleteRenderInputParams) => {
              if (selectedOption) {
                params.InputProps = {
                  ...params.InputProps,
                  startAdornment: (
                    <ToolTipComponent
                      position="top"
                      content={selectedOption.name ? untranslated(selectedOption.name) : undefined}
                    >
                      <span
                        style={getStatusBtnStyle(selectedOption.category)}
                        className="truncate rounded px-3 py-0.5 text-sm text-white max-w-full cursor-pointer"
                        onClick={(e) => {
                          const input = e.currentTarget.closest(".MuiAutocomplete-root")?.querySelector("input");
                          if (input) {
                            input.focus();
                            input.dispatchEvent(new MouseEvent("mousedown", { bubbles: true }));
                          }
                        }}
                      >
                        {selectedOption.name}
                      </span>
                    </ToolTipComponent>
                  ),
                };
                params.inputProps = {
                  ...params.inputProps,
                  style: {
                    ...params.inputProps.style,
                    opacity: 0,
                    width: 0,
                    minWidth: 0,
                    padding: 0,
                    position: "absolute",
                  },
                };
              }
              return <TextFieldComponent {...params} />;
            }}
            onKeyDown={(e) => e.stopPropagation()}
            slotProps={{ listbox: { sx: { maxHeight: "15.625rem", overflowY: "auto", minWidth: "max-content" } } }}
            sx={{ flexGrow: 1, cursor: "pointer", "& .MuiInputBase-root": { cursor: "pointer" } }}
          />
        </div>
      );
    };

    const fileStatusFormatter = (
      row: number,
      cell: number,
      value: unknown,
      columnDef: unknown,
      dataContext: FileInReviewRow,
    ) => {
      const container = document.createElement("div");
      const checklistAssignments = dataContext.checklistAssignments || [];

      const root = portalCreateRoot(container);
      flushSync(() => {
        root.render(
          <div className="flex flex-row items-center gap-4" onClick={(e) => e.stopPropagation()}>
            <FileStatusDropdown args={dataContext} />
            {dataContext.statuses.length > 1 || dataContext.signatures?.length ? (
              <DetailsIcon
                size="24"
                color="#192048"
                onClick={() => openStatusHistoryFromData(dataContext)}
                className="cursor-pointer"
              />
            ) : (
              <ToolTipComponent position="left" showTipPointer content={t("There is no status history for this file")}>
                <span className="flex items-center cursor-not-allowed opacity-50">
                  <DetailsIcon size="20" color="#192048" onClick={undefined} />
                </span>
              </ToolTipComponent>
            )}
            <Box
              onClick={() => openReferencedFilesAttachedFromData(dataContext)}
              className="w-14 h-6 cursor-pointer items-center justify-center flex rounded-2xl border border-primary_text"
            >
              <IconComponent name="smallAttachmentIcon" width="1rem" height="1rem" />{" "}
              {_.get(dataContext, "referencedFiles", []).length > 0 ? _.size(dataContext.referencedFiles) : ""}{" "}
            </Box>

            {reviewDataHelperRef.current.reviewFlow.checklists &&
              reviewDataHelperRef.current.reviewStatus != "Draft" && (
                <Box
                  onClick={() => openChecklistModalFromData(dataContext)}
                  className="w-14 h-6 cursor-pointer items-center justify-center flex gap-1 rounded-2xl border border-primary_text"
                >
                  <IconComponent name="checklists" width="1.125rem" height="0.875rem" />
                  {checklistAssignments.length > 0 && <span>{checklistAssignments.length}</span>}
                </Box>
              )}
          </div>,
        );
      });
      return container;
    };

    const getFileSizeOptionIndex = (size: number): number => {
      const index = size > (fileSizeOptions[1]?.divideValue ?? 0) ? 1 : 0;
      return index;
    };

    const fileSizeFormatter = (
      row: number,
      cell: number,
      value: unknown,
      columnDef: unknown,
      dataContext: FileInReviewRow,
    ) => {
      const size = dataContext.details.size || 0;
      const fileSizeIndex = getFileSizeOptionIndex(size);
      const fileSizeOption = fileSizeOptions[fileSizeIndex];
      const sizeValue = parseFloat((size / (fileSizeOption?.divideValue ?? 1)).toString()).toFixed(2);
      return `${sizeValue} ${fileSizeOption?.name ?? ""}`;
    };

    const areIssuesPresentFormatter = (
      row: number,
      cell: number,
      value: unknown,
      columnDef: unknown,
      dataContext: FileInReviewRow,
    ) => {
      if (dataContext.details.countOfIssues) {
        const container = document.createElement("div");
        portalCreateRoot(container).render(<IconComponent name="issues" width="1.5rem" height="1.5rem" />);
        return container;
      }
      return "";
    };

    const fileNameFormatter = (
      row: number,
      cell: number,
      value: unknown,
      columnDef: unknown,
      dataContext: FileInReviewRow,
    ) => {
      const name = dataContext.details.name || "";
      const container = document.createElement("div");
      container.className = "truncate";
      portalCreateRoot(container).render(
        <ToolTipComponent position="bottom" content={untranslated(name)}>
          <span className="truncate">{name}</span>
        </ToolTipComponent>,
      );
      return container;
    };

    const fileVersionFormatter = (
      row: number,
      cell: number,
      value: unknown,
      columnDef: unknown,
      dataContext: FileInReviewRow,
    ) => {
      const { versionNumber, isLatestVersion } = dataContext.details;
      const container = document.createElement("div");
      container.className = "flex flex-row gap-1 items-center";

      if (!isLatestVersion) {
        portalCreateRoot(container).render(
          <div className="flex flex-row gap-1 items-center">
            {`V${versionNumber}`}
            <ToolTipComponent
              showTipPointer
              position="bottom"
              content={t(
                "You are currently working on an older version of this file. There is a newer version available",
              )}
            >
              <span className="flex items-center">
                <DetailsIcon size="24" color="#ff0000" className={undefined} onClick={undefined} />
              </span>
            </ToolTipComponent>
          </div>,
        );
      } else {
        container.textContent = `V${versionNumber}`;
      }
      return container;
    };

    const fileActivityHeader = () => (
      <div className="w-full flex flex-row justify-between items-center rounded-md">
        <Text className="font-semibold text-lg">{t("File History")}</Text>
      </div>
    );

    const fileActivityContent = () => (
      <div className=" h-600">
        <ResourceActivityInReview reviewId={reviewId} spaceId={spaceId} projectId={projectId} />
      </div>
    );

    const updateFileModalHeader = () => (
      <Text className="w-full text-2xl font-bold rounded-t-2">{t("Update Version")}</Text>
    );

    const updateFileModalContent = () => (
      <div className="flex flex-col gap-4 ps-8 pt-4 pe-8">
        <Text className="whitespace-normal break-words">
          {t("Are you sure you want to update the selected file(s) to their latest version ?")}
        </Text>
        <Text className="whitespace-normal break-words">
          <Text className="font-semibold">{t("Note")}</Text>
          {":"}
          <Text className="whitespace-normal break-words">
            {t(
              "A notification will be sent to the previous reviewers stating that their has been an update in the files of this review",
            )}
          </Text>
        </Text>
      </div>
    );

    const updateFileModalFooter = () => (
      <div className="flex flex-row gap-4 items-center justify-start">
        <ButtonComponent
          onClick={updateFilesInReviewHandler}
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          loading={isLoadingFileUpdate}
        >
          <Text>{t("Confirm")}</Text>
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeUpdateFileModal}>
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );

    const removeFileModalHeader = () => (
      <Text className="w-full text-2xl font-bold rounded-t-2">{t("Remove File(s)")}</Text>
    );

    const removeFileModalContent = () => (
      <Text noWrap>
        {t("Do you want to remove the selected resources and their associated issue(s) from the review?")}
      </Text>
    );

    const removeFileModalFooter = () => (
      <div className="flex flex-row gap-4 items-center justify-start">
        <ButtonComponent
          onClick={removeFilesFromReview}
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          loading={isLoadingRemoveFiles}
        >
          <Text>{t("Confirm")}</Text>
        </ButtonComponent>
        <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={closeRemoveFileModal}>
          <Text>{t("Cancel")}</Text>
        </ButtonComponent>
      </div>
    );

    const columnDefinitions = useMemo(
      () => [
        {
          id: "fileVersionId",
          field: "fileVersionId",
          name: "",
          width: 0,
          excludeFromColumnPicker: true,
          minWidth: 0,
          maxWidth: 0,
          initiallyHidden: true,
          disableFiltering: true,
          disableSorting: true,
          disableGrouping: true,
        },
        {
          id: "openInViewer",
          field: "",
          name: "",
          width: 50,
          minWidth: 50,
          excludeFromColumnPicker: true,
          maxWidth: 50,
          formatter: openFileInViewerFormatter,
          disableFiltering: true,
          disableSorting: true,
          disableGrouping: true,
        },
        {
          id: "issuesPresent",
          field: "",
          name: "",
          width: 50,
          minWidth: 50,
          maxWidth: 50,
          excludeFromColumnPicker: true,
          formatter: areIssuesPresentFormatter,
          disableFiltering: true,
          disableSorting: true,
          disableGrouping: true,
        },
        {
          id: "resourceType",
          field: "resourceType",
          name: "",
          width: 0,
          minWidth: 0,
          excludeFromColumnPicker: true,
          maxWidth: 0,
          initiallyHidden: true,
          disableFiltering: true,
          disableSorting: true,
          disableGrouping: true,
        },
        {
          id: "name",
          field: "details.name",
          name: t("Name"),
          width: 200,
          minWidth: 200,
          formatter: fileNameFormatter,
          filterType: "Text",
        },
        {
          id: "fileType",
          field: "fileType",
          name: t("Type"),
          width: 80,
          minWidth: 80,
          formatter: fileTypeFormatter,
          filterType: "Text",
          _valueGetter: (row: FileInReviewRow) => row.details.fileType || "",
        },
        {
          id: "versionNumber",
          field: "versionNumber",
          name: t("Version"),
          width: 100,
          minWidth: 80,
          formatter: fileVersionFormatter,
          filterType: "Number",
          _valueGetter: (row: FileInReviewRow) => (row.details.versionNumber ? `V${row.details.versionNumber}` : ""),
        },
        {
          id: "size",
          field: "size",
          name: t("Size"),
          width: 100,
          minWidth: 80,
          formatter: fileSizeFormatter,
          disableFiltering: true,
          _valueGetter: (row: FileInReviewRow) => {
            const size = row.details.size || 0;
            const fileSizeIndex = getFileSizeOptionIndex(size);
            const fileSizeOption = fileSizeOptions[fileSizeIndex];
            const sizeValue = parseFloat((size / (fileSizeOption?.divideValue ?? 1)).toString()).toFixed(2);
            return `${sizeValue} ${fileSizeOption?.name ?? ""}`;
          },
        },
        {
          id: "status",
          field: "status",
          name: t("Status"),
          width: 550,
          minWidth: 450,
          formatter: fileStatusFormatter,
          filterType: "Text",
          _valueGetter: (row: FileInReviewRow) => row.statuses.slice(-1)[0]?.name || "",
        },
      ],

      [],
    );

    const getDataGrid = () => (
      <div
        className={
          isLoadingComplete
            ? "h-full w-full overflow-auto flex flex-col"
            : "h-full w-full overflow-auto flex flex-col invisible"
        }
      >
        <SlickGrid1
          ref={dataGrid}
          remoteDataSource={getResourcesInReviewUrl}
          query={filesQuery}
          toolbar={toolbar}
          toolbarClickHandler={toolbarClickHandler}
          // BLOCKED ON THE DECLARATION: `ColumnDefinition<T, K extends keyof T>` types `field` as a KEY
          // of the row, so `typedColumns()` cannot express this grid's columns — `details.name` is a
          // dotted path and `fileType`/`size`/`versionNumber`/`status` are computed columns served by
          // `formatter`/`_valueGetter`, none of which are members of `FileInReviewRow`. Until
          // SlickGrid1 admits a display-only `field`, there is no constructor that accepts them.
          columnDefinitions={
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- see the note above
            columnDefinitions as ColumnDefinition<FileInReviewRow>[] as ValidatedColumnDefinition<FileInReviewRow>[]
          }
          onSelectedRowIdsChanged={selectionEvents}
          setGridReady={dataBound}
          rowHeight={50}
          gridHeight="100%"
          enableColumnChooser
          infiniteScroll
          enableFilteringOption
          enableSortingOption
          enableGroupingOption
          excelExport
          hideNoDataIllustration
          propertiesPanel={showProperties}
          propertiesDisabled={propertiesBtnDisabled}
          gridOptions={{ forceFitColumns: true }}
        />
      </div>
    );

    return (
      <div className="flex flex-row w-full h-full overflow-auto">
        {getDataGrid()}
        {fileProperties.show && showProperties && (
          <FilePropertiesInReviews {...fileProperties.data!} closeProperties={closeFileProperties} />
        )}
        {/* `defaultSignatureModalState` seeds this state, so `reviewId` is `null` and both statuses are
            `{}` at rest; `fileStatusChanged` fills every field in the same call that sets `show`, which
            is what the guard below states instead of asserting the at-rest shape away. */}
        {signatureModal.show && signatureModal.reviewId && signatureModal.type && (
          <ChangeFileStatusModal
            reviewId={signatureModal.reviewId}
            statusTo={toStatus(signatureModal.statusTo)}
            statusFrom={toStatus(signatureModal.statusFrom ?? undefined)}
            resources={signatureModal.resources ?? []}
            type={signatureModal.type}
            requireMandatoryComment={signatureModal.requireMandatoryComment}
            closeModal={closeSignatureModal}
          />
        )}
        {bulkTransitionModal.show && bulkTransitionModal.data?.reviewId && bulkTransitionModal.data.type && (
          <ChangeFileStatusModal
            reviewId={bulkTransitionModal.data.reviewId}
            // A bulk transition has no preselected target — the modal's own dropdown is where the user
            // picks one — so the "no status" sentinel is what it starts from.
            statusTo={toStatus(undefined)}
            statusFrom={toStatus(bulkTransitionModal.data.statusFrom)}
            resources={bulkTransitionModal.data.resources}
            type={bulkTransitionModal.data.type}
            requireMandatoryComment={bulkTransitionModal.data.requireMandatoryComment}
            closeModal={closeBulkTransitionModal}
          />
        )}
        {addSignatureToResourceModal.show &&
          addSignatureToResourceModal.data.resources &&
          addSignatureToResourceModal.data.reviewId && (
            <AddSignatureToResource
              closeModal={closeAddSignatureModal}
              resources={addSignatureToResourceModal.data.resources}
              reviewId={addSignatureToResourceModal.data.reviewId}
            />
          )}
        {addFileModal.show && addFileModal.stage === 1 && (
          <FileManagerAsPopup
            projectId={projectId}
            workspaceId={spaceId}
            attachSelectedBtnName={t("Attach Files")}
            id="fileManagerAsPopup"
            handleFileSelectedButtonClick={resourceSelectionCallback}
            handleFileSelectedCancelClick={closeAddFileModal}
            hideIssues
            hideTasks
            hideSchedules
            hideEstimates
            hideReviews
            hideMergeFiles
            hideContracts
            hideClashTests
            openThisSpaceByDefault
            isLoadingAddFiles={isLoadingAddFiles}
            setIsLoadingAddFiles={setIsLoadingAddFiles}
            disableFiles={
              dataGrid?.current
                ? dataGrid.current.currentViewData!.map((record: FileInReviewRow) => record.fileVersionId)
                : []
            }
            minimumFiles={1}
          />
        )}
        {addFileModal.show && addFileModal.stage === 2 && reviewData.reviewFlow.checklists && addFileModal.data && (
          <ChecklistAssignmentModal
            reviewData={checklistFlowData!}
            setReviewData={setChecklistFlowData}
            onNext={handleChecklistAssignment}
            onBack={backToFileSelection}
          />
        )}
        {removeFileModal.show && (
          <ModalStateDialogComponent
            isVisible
            allowModalPadding={false}
            width="31.25rem"
            headerTitle={removeFileModalHeader()}
            modelContent={removeFileModalContent()}
            footerContent={removeFileModalFooter()}
            onCancel={closeRemoveFileModal}
          />
        )}
        {updateFileConfirmModal.show && updateFileConfirmModal.fileVersionIds.length && (
          <ModalStateDialogComponent
            isVisible
            allowModalPadding={false}
            width="31.25rem"
            headerTitle={updateFileModalHeader()}
            modelContent={updateFileModalContent()}
            footerContent={updateFileModalFooter()}
            onCancel={closeUpdateFileModal}
          />
        )}

        {fileActivityModal.show && (
          <ModalStateDialogComponent
            isVisible
            allowModalPadding={false}
            width="62.5rem"
            headerTitle={fileActivityHeader()}
            modelContent={fileActivityContent()}
            onCancel={closeFileActivityModal}
          />
        )}
        {statusHistoryModal.show && (
          <ResourceStatusHistoryInReview
            statuses={statusHistoryModal.statuses || []}
            closeModal={closeStatusHistory}
            showSignatureHistory
            signatures={statusHistoryModal.signatures || []}
          />
        )}
        {/* Both modals declare a 24-hex `ObjectIdString`; the row that opens them always carries one,
            so the id is required by the guard rather than defaulted to an empty string the wire
            rejects. */}
        {openAttachmentModal.show && openAttachmentModal.fileVersionId && (
          <Attachments
            closeAttachmentModal={closeAttachmentModal}
            attachments={openAttachmentModal.attachments || []}
            fileVersionId={openAttachmentModal.fileVersionId}
            reviewId={reviewId}
          />
        )}

        {checklistModal.show && checklistModal.fileVersionId && parsedProjectId && (
          <ChecklistModal
            fileVersionId={checklistModal.fileVersionId}
            closeModal={closeChecklistModal}
            reviewId={reviewId}
            // ChecklistModal declares all three flags optional and only ever asks for a full refresh.
            refreshData={(refreshFiles = true, refreshIssues = true, refreshReviewData = true) =>
              refreshData(refreshFiles, refreshIssues, refreshReviewData)
            }
            projectId={parsedProjectId}
          />
        )}
        <CreateTransmittal
          isOpen={openCreateTransmittalModal}
          onClose={() => setOpenCreateTransmittalModal(false)}
          projectId={projectId}
          selectedSpaceId={spaceId}
          selectedReferences={processedTransmittalRecords}
          reviewId={reviewId}
        />
      </div>
    );
  },
);

FilesInReview.displayName = "FilesInReview";
export default FilesInReview;
